import { useCallback, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const IS_DEV = process.env.NODE_ENV !== "production";

const logger = {
  warn: (...args) => { if (IS_DEV) console.warn(...args); },
  error: (...args) => { if (IS_DEV) console.error(...args); },
};

const ERROR_MESSAGE =
  "Désolé, une erreur est survenue. Merci de contacter le 04.93.62.00.29.";
const NETWORK_ERROR =
  "Connexion perdue. Rechargez la page ou contactez-nous au 04.93.62.00.29.";

/**
 * Parse one SSE chunk (delimited by blank line) and return {event, data}.
 * Returns null if the chunk cannot be parsed.
 */
function parseSSEChunk(chunk) {
  if (!chunk.trim()) return null;
  let event = "message";
  let dataStr = "";
  for (const line of chunk.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
  }
  if (!dataStr) return null;
  try {
    return { event, data: JSON.parse(dataStr) };
  } catch (err) {
    logger.warn("[useAssistantStream] Skipping partial SSE chunk", err);
    return null;
  }
}

/**
 * Update the last assistant message with a transform function.
 */
function patchLastAssistant(setMessages, patch) {
  setMessages((m) =>
    m.map((msg, idx) =>
      idx === m.length - 1 && msg.role === "assistant"
        ? { ...msg, ...patch(msg) }
        : msg
    )
  );
}

/**
 * Custom hook encapsulating all Assistant streaming logic:
 *  - message state
 *  - SSE parsing
 *  - session persistence (in-memory)
 *  - error handling
 */
export default function useAssistantStream() {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState("");

  const reset = useCallback(() => {
    setMessages([]);
    setSessionId("");
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!trimmed || isLoading) return;

      const userId = `u-${Date.now()}`;
      const assistantId = `a-${Date.now()}`;
      setMessages((m) => [
        ...m,
        { id: userId, role: "user", content: trimmed },
        { id: assistantId, role: "assistant", content: "", streaming: true },
      ]);
      setIsLoading(true);

      try {
        const res = await fetch(`${API}/assistant/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId || undefined,
            message: trimmed,
          }),
        });
        if (!res.ok || !res.body) throw new Error("Network error");

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const chunks = buffer.split("\n\n");
          buffer = chunks.pop();

          for (const chunk of chunks) {
            const parsed = parseSSEChunk(chunk);
            if (!parsed) continue;
            const { event, data } = parsed;

            if (event === "session" && data.session_id) {
              setSessionId(data.session_id);
            } else if (event === "error") {
              patchLastAssistant(setMessages, () => ({
                content: ERROR_MESSAGE,
                streaming: false,
              }));
            } else if (event === "done") {
              patchLastAssistant(setMessages, () => ({ streaming: false }));
            } else if (data.delta) {
              patchLastAssistant(setMessages, (msg) => ({
                content: (msg.content || "") + data.delta,
              }));
            }
          }
        }
      } catch (err) {
        logger.error("[useAssistantStream] sendMessage failed", err);
        patchLastAssistant(setMessages, () => ({
          content: NETWORK_ERROR,
          streaming: false,
        }));
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, sessionId]
  );

  return { messages, isLoading, sendMessage, reset };
}
